/**
 * y-partyserver sync client — opens a WS to `/sync/<key>/ws` and binds
 * a `Y.Doc` to the connection.
 *
 * Architecture refs:
 *   §4.4 — CRDT sync topology. One DO per addressable y-doc, keyed under
 *          a per-kind prefix:
 *            draft:<branch-uuid>
 *            obs-attempt:<attempt-uuid>
 *            obs-artefact:<artefact-uuid>
 *            obs-batch:<batch-uuid>        (optionally suffixed :<reopen-nonce>)
 *          Wire protocol is standard Yjs WebSocket.
 *   §4.12 — Auth. Session cookie rides with the WS upgrade request. The
 *          browser attaches cookies to WS URLs on the same origin
 *          automatically, so this client does not touch `document.cookie`.
 *   §4.5 — Client-side persistence. When `storageAdapter` is provided, the
 *          doc is hydrated from local IDB BEFORE the WS connects (so the
 *          first paint is from cache), then every `update` event is appended
 *          to the adapter. Spike 0.C: state enters the doc only through
 *          `Y.applyUpdate`.
 *
 * Under the hood: `y-partyserver/provider` exports `WebsocketProvider`
 * (a fork of `y-websocket` tuned for partyserver). We wrap it so callers
 * never see the provider directly and the URL-construction contract lives
 * in one place.
 */

import { Doc } from "yjs";
import { WebsocketProvider } from "y-partyserver/provider";
import { isUuid } from "@proof/domain";
import type { YjsStorageAdapter } from "../storage/adapter.js";
import { hydrateDocFromStorage, attachUpdatePersistence } from "./persistence.js";

// ─── Y-doc key shapes (mirror architecture §4.4) ──────────────────────────────

export type YDocKind = "draft" | "obs-attempt" | "obs-artefact" | "obs-batch";

/**
 * An opaque y-doc key. Shape: `<kind>:<uuid>` or `obs-batch:<uuid>:<nonce>`.
 * Validated by `parseYDocKey` — callers should prefer `buildYDocKey` so the
 * shape cannot drift.
 */
export type YDocKey = string;

export interface ParsedYDocKey {
  readonly kind: YDocKind;
  readonly uuid: string;
  /** Only set for `obs-batch` keys that include a reopen nonce. */
  readonly nonce?: string;
}

const KIND_SET: ReadonlySet<YDocKind> = new Set([
  "draft",
  "obs-attempt",
  "obs-artefact",
  "obs-batch",
]);

export function buildYDocKey(kind: YDocKind, uuid: string, nonce?: string): YDocKey {
  if (!isUuid(uuid)) throw new Error(`buildYDocKey: invalid uuid ${uuid}`);
  if (kind === "obs-batch" && nonce !== undefined) return `${kind}:${uuid}:${nonce}`;
  if (nonce !== undefined && kind !== "obs-batch") {
    throw new Error(`buildYDocKey: nonce is only allowed for obs-batch`);
  }
  return `${kind}:${uuid}`;
}

export function parseYDocKey(key: YDocKey): ParsedYDocKey | null {
  const parts = key.split(":");
  if (parts.length < 2 || parts.length > 3) return null;
  const [kindRaw, uuid, nonce] = parts;
  if (!kindRaw || !uuid) return null;
  if (!KIND_SET.has(kindRaw as YDocKind)) return null;
  const kind = kindRaw as YDocKind;
  if (!isUuid(uuid)) return null;
  if (nonce !== undefined && kind !== "obs-batch") return null;
  return nonce !== undefined ? { kind, uuid, nonce } : { kind, uuid };
}

// ─── Client ───────────────────────────────────────────────────────────────────

export interface SyncClientOptions {
  /** y-partyserver origin. Usually the same as `apps/api`. */
  baseUrl: string;
  /** Path prefix where y-partyserver is mounted. Defaults to `/sync`. */
  syncPath?: string;
  /** Override the global WebSocket — tests + polyfills. */
  WebSocketPolyfill?: typeof WebSocket;
  /** If false, the provider does not open the WS on construction. Defaults
   *  to true. The caller can always call `.connect()` later. */
  connect?: boolean;
  /**
   * Optional client-side persistence adapter (architecture §4.5).
   *
   * When provided:
   *   1. The doc is hydrated from local IDB BEFORE the WS provider connects
   *      (immediate first-paint from cache; WS sync reconciles on top).
   *   2. Every `update` event on the doc is appended to the adapter.
   *   3. LRU eviction is applied (MAX_CACHED_DOCS from persistence.ts).
   *
   * Spike 0.C contract: all state enters the doc through `Y.applyUpdate`.
   * The WS provider's updates merge on top of the locally hydrated state via
   * standard Yjs CRDT semantics — no doc reset on connect.
   *
   * When undefined, the client behaves as before (WS-only, no local cache).
   */
  storageAdapter?: YjsStorageAdapter;
}

export interface YPartyClient {
  /** The bound y-doc. Callers attach their own text / map observers. */
  readonly doc: Doc;
  /** Underlying provider — exposed for advanced consumers (awareness,
   *  reconnect handlers). Prefer the typed helpers below where possible. */
  readonly provider: WebsocketProvider;
  /** True once the provider has synced with the server (initial state
   *  received). Matches `provider.synced`. */
  readonly synced: boolean;
  /**
   * Resolves once local persistence hydration is complete (before the WS
   * provider connects). When no `storageAdapter` was provided, resolves
   * immediately with 0.
   *
   * Callers that need to render content before WS sync should `await` this.
   * The WS provider connects immediately after hydration finishes.
   */
  readonly hydrated: Promise<number>;
  /** Close the WS and free resources. Subsequent calls are no-ops. */
  destroy(): void;
}

/**
 * Open a y-partyserver WS connection for the given key and return a
 * client wrapper bound to a fresh `Y.Doc`.
 *
 * The HTTP-to-WS URL is constructed as:
 *   `<baseUrl>/<syncPath>/<key>/ws`
 *
 * y-partyserver's `WebsocketProvider` takes (serverUrl, roomname, doc) and
 * constructs `serverUrl/roomname` internally — we pass `isPrefixedUrl: true`
 * so it uses our fully-built URL unchanged.
 *
 * When `opts.storageAdapter` is provided:
 *   1. The provider is created with `connect: false`.
 *   2. Persisted updates are hydrated into the doc via `Y.applyUpdate`.
 *   3. The `update` persistence listener is attached (LRU eviction included).
 *   4. The provider connects AFTER hydration — the caller's `opts.connect`
 *      value is respected (if explicitly `false`, the WS never opens).
 *
 * This ordering guarantees the doc has locally-cached content before the WS
 * handshake, so the first render comes from IDB rather than waiting for the
 * server round-trip (~500–1000 ms). WS sync then merges on top via standard
 * Yjs CRDT semantics — no doc reset on connect.
 */
export function createSyncClient(key: YDocKey, opts: SyncClientOptions): YPartyClient {
  if (!parseYDocKey(key)) {
    throw new Error(`createSyncClient: invalid y-doc key ${key}`);
  }
  const baseUrl = opts.baseUrl.replace(/\/+$/, "");
  const syncPath = (opts.syncPath ?? "/sync").replace(/^\/?/, "/").replace(/\/+$/, "");
  // Map http(s) → ws(s) for the WS URL.
  const wsBase = baseUrl.replace(/^https:\/\//, "wss://").replace(/^http:\/\//, "ws://");
  const url = `${wsBase}${syncPath}/${encodeURIComponent(key)}/ws`;

  const doc = new Doc();

  // ── Persistence wiring ──────────────────────────────────────────────────────
  // When a storageAdapter is provided we defer the WS connect until after
  // local hydration. `callerWantsConnect` captures the caller's intent so we
  // respect `connect: false` (e.g. test stubs or deferred-open patterns).
  const callerWantsConnect = opts.connect ?? true;
  const adapter = opts.storageAdapter;

  let detachPersistence: (() => void) | null = null;

  // Explicit destroyed sentinel — do NOT reuse provider.shouldConnect for this.
  // y-partyserver constructs with `shouldConnect = connect`, so when we pass
  // `connect: false` (adapter path) it starts as false and the old guard
  // `if (!provider.shouldConnect)` was ALWAYS true, silently skipping both
  // `attachUpdatePersistence` and `provider.connect()` on every hydration.
  let destroyed = false;

  const providerOpts: NonNullable<ConstructorParameters<typeof WebsocketProvider>[3]> = {
    // If we have an adapter, always create the provider disconnected and then
    // connect after hydration (unless the caller explicitly passed false).
    connect: adapter ? false : callerWantsConnect,
    isPrefixedUrl: true,
  };
  if (opts.WebSocketPolyfill !== undefined) {
    providerOpts.WebSocketPolyfill = opts.WebSocketPolyfill;
  }
  // `WebsocketProvider(serverUrl, roomname, doc, opts)` — with
  // `isPrefixedUrl: true`, the provider appends nothing to `serverUrl`, so
  // `roomname` is effectively a tag only. Passing the key still keeps
  // diagnostics / broadcast-channel names unique per doc.
  const provider = new WebsocketProvider(url, key, doc, providerOpts);

  // `hydrated` resolves once local IDB hydration is done (before WS connect).
  // When there is no adapter it resolves immediately with 0.
  const hydrated: Promise<number> = adapter
    ? hydrateDocFromStorage(key, doc, adapter)
        .then((count) => {
          // Attach the persistence subscription AFTER hydration so we do not
          // double-persist the updates we just loaded.
          if (destroyed) {
            // destroy() was called before hydration finished — the provider is
            // already torn down; do nothing.
            return count;
          }
          detachPersistence = attachUpdatePersistence(key, doc, adapter);
          // Now open the WS (if the caller wanted it open).
          if (callerWantsConnect) {
            provider.connect();
          }
          return count;
        })
        .catch((err) => {
          // Hydration error — still connect to the WS so the user is not
          // completely offline. Log for diagnostics.
          console.error({ event: "yjs_hydrate_fatal", key, err: String(err) });
          if (callerWantsConnect && !destroyed) {
            provider.connect();
          }
          return 0;
        })
    : Promise.resolve(0);

  return {
    doc,
    provider,
    hydrated,
    get synced() {
      return provider.synced;
    },
    destroy() {
      if (destroyed) return; // idempotent
      destroyed = true;
      if (detachPersistence) {
        detachPersistence();
        detachPersistence = null;
      }
      provider.destroy();
      doc.destroy();
    },
  };
}
